import i18n from "@/i18n";
import SocketController from "@/services/Socket/socket.controller";
import {
  FullSingleQuote,
  fullSingleQuoteSchema,
  SimpleQuotation,
  simpleQuotationSchema,
} from "@/types";
import {
  validateArrayWithSchema,
  validateWithSchema,
} from "@/utils/dataValidators.zod";

export async function fetchAllUsersQuotes(): Promise<
  | {
      rStatus: "success";
      rData: SimpleQuotation[];
    }
  | {
      rStatus: "error";
      rData: string;
    }
> {
  try {
    const response = await SocketController.prototype.sendCommand(
      "userQuotes",
      {},
      true
    );

    if (response.status === 200) {
      const validatedData = validateArrayWithSchema<SimpleQuotation>(
        response.data,
        simpleQuotationSchema,
        "Simple Quotation"
      );

      if (validatedData) {
        return {
          rStatus: "success",
          rData: validatedData,
        };
      } else {
        console.error("Invalid quote data received", response.data);
        return {
          rStatus: "error",
          rData: i18n.t("Invalid quote data received"),
        };
      }
    }

    if (response.status === 204) {
      return {
        rStatus: "success",
        rData: [],
      };
    }

    if (response.status === 500) {
      return {
        rStatus: "error",
        rData: i18n.t("Internal server error. Please try again later."),
      };
    }

    if (response.status === 0) {
      return {
        rStatus: "error",
        rData: i18n.t("Network error or timeout"),
      };
    }

    console.error("Unexpected status code:", response.status);
    return {
      rStatus: "error",
      rData: i18n.t("Failed to fetch user quotes"),
    };
  } catch (error) {
    console.error("Error fetching user quotes:", error);
    return {
      rStatus: "error",
      rData: i18n.t("Failed to fetch user quotes"),
    };
  }
}

export async function fetchSingleQuote(quoteID: string): Promise<
  | {
      rStatus: "success";
      rData: FullSingleQuote;
    }
  | {
      rStatus: "error";
      rData: string;
    }
> {
  try {
    const response = await SocketController.prototype.sendCommand(
      "quoteByID",
      { quoteID },
      true
    );

    if (response.status === 200) {
      const validatedData = validateWithSchema<FullSingleQuote>(
        response.data,
        fullSingleQuoteSchema,
        "Full Single Quote"
      );

      if (validatedData) {
        return {
          rStatus: "success",
          rData: validatedData,
        };
      } else {
        console.error("Invalid single quote data received", response.data);
        return {
          rStatus: "error",
          rData: i18n.t("Invalid single quote data received"),
        };
      }
    }

    if (response.status === 204) {
      return {
        rStatus: "error",
        rData: i18n.t("No quote found with the provided ID"),
      };
    }

    if (response.status === 0) {
      return {
        rStatus: "error",
        rData: i18n.t("Network error or timeout"),
      };
    }

    return {
      rStatus: "error",
      rData: i18n.t("Failed to fetch single quote"),
    };
  } catch (error) {
    console.error("Error fetching single quote:", error);
    return {
      rStatus: "error",
      rData: i18n.t("Failed to fetch single quote"),
    };
  }
}

export async function sendUserApproveRejectQuote(
  quoteDbID: string,
  action: "accept" | "reject",
  reason?: string
): Promise<
  | {
      rStatus: "success";
    }
  | {
      rStatus: "error";
      rData: string;
    }
> {
  try {
    const response = await SocketController.prototype.sendCommand(
      "quoteApproval",
      {
        quoteID: quoteDbID,
        approvalStatus: action === "accept" ? "accepted" : "rejected",
        note: reason,
        deliveryMethod: undefined,
      },
      true
    );

    if (response.status === 200 || response.status === 202) {
      return {
        rStatus: "success",
      };
    }

    if (response.status === 204) {
      return {
        rStatus: "error",
        rData: i18n.t("No quote found with the provided ID"),
      };
    }

    if (response.status === 209) {
      return {
        rStatus: "error",
        rData: i18n.t("Quote already processed"),
      };
    }

    if (response.status === 400) {
      return {
        rStatus: "error",
        rData: i18n.t("Invalid request. Please contact support."),
      };
    }

    if (response.status === 401 || response.status === 403) {
      return {
        rStatus: "error",
        rData: i18n.t("Unauthorized action. Please log in again."),
      };
    }

    if (response.status === 422) {
      return {
        rStatus: "error",
        rData: i18n.t("Quote expired or invalid. Please request a new quote."),
      };
    }

    if (response.status === 0) {
      return {
        rStatus: "error",
        rData: i18n.t("Network error or timeout"),
      };
    }

    if (response.status === 500) {
      return {
        rStatus: "error",
        rData: i18n.t("Internal server error. Please try again later."),
      };
    }

    return {
      rStatus: "error",
      rData: i18n.t("Failed to process quote action"),
    };
  } catch (error) {
    console.error("Error processing quote action:", error);
    return {
      rStatus: "error",
      rData: i18n.t("Failed to process quote action"),
    };
  }
}
